/**
 * Pin to this project (07 Workshop, Pin something, 12:50666): what to pin is
 * picked by kind (Universe, Strategy, Signals, Trends) on a segmented control,
 * or A link beside it; found in a list inside the dialog (the pick fills with
 * the accent and a check), then labelled. The same call as before pins it
 * (useWorkshopContextItems.addItem). The frame has no note field, so the
 * dialog no longer asks for one.
 */
import { useMemo, useState } from "react";
import { Check, Link2 } from "lucide-react";
import { KitInput } from "@/app/routes/dashboard/components/kit/Input";
import { KitSegmentedControl } from "@/app/routes/dashboard/components/kit/SegmentedControl";
import { useBrandUniverseGraph } from "@/app/routes/dashboard/api/useBrandUniverseGraph";
import { useStrategies } from "@/app/routes/dashboard/strategy/api/useStrategies";
import { usePermissions } from "@/hooks/usePermissions";
import { useMarketSignals } from "@/app/routes/dashboard/market-signals/api/useMarketSignals";
import { useTrends } from "@/app/routes/dashboard/trends/api/useTrends";
import { timeAgo } from "@/app/routes/dashboard/trends/components/trendMeta";
import { WORKSHOP_CONTEXT_KINDS, type WorkshopContextItemKind } from "@/types/workshop.type";
import type { AddContextVars } from "./ContextTab";
import WorkshopDialog from "./WorkshopDialog";

/** The segment names, as the frame writes them. */
const KIND_SEGMENT: Record<WorkshopContextItemKind, string> = {
  universe_fact: "Universe",
  strategy: "Strategy",
  market_signal: "Signals",
  trend: "Trends",
  document: "Document",
  url: "A link",
};

// No "document" here: a file is uploaded to the project (the Files group), and
// pinning another chat's attachment by id would pull a file that belongs to
// that chat into this project. Documents pinned before stay readable. A link is
// its own button beside the segments, not a segment.
const KIND_ITEMS = WORKSHOP_CONTEXT_KINDS.filter((kind) => kind !== "document" && kind !== "url").map((kind) => ({
  value: kind,
  label: KIND_SEGMENT[kind],
}));

/** Sources a Guest (a brand's client) can't read: strategy and signals are team surfaces. */
const GUEST_HIDDEN_KINDS = new Set<WorkshopContextItemKind>(["strategy", "market_signal"]);

type PickerOption = {
  id: string;
  title: string;
  subtitle?: string;
};

function PickerList({
  options,
  isLoading,
  selectedId,
  onPick,
  emptyText,
}: {
  options: PickerOption[];
  isLoading: boolean;
  selectedId: string | null;
  onPick: (option: PickerOption) => void;
  emptyText: string;
}) {
  if (isLoading) {
    return (
      <div className="ws-pin-list" aria-busy="true" aria-label="Loading">
        {[0, 1].map((i) => (
          <span key={i} className="ws-pin-pick ws-pin-pick--skel" aria-hidden="true">
            <span className="ws-skel ws-skel--line" style={{ width: i ? "48%" : "62%", height: 13 }} />
            <span className="ws-skel ws-skel--line" style={{ width: "88%" }} />
          </span>
        ))}
      </div>
    );
  }
  if (options.length === 0) {
    return (
      <div className="ws-pin-list">
        <p className="ws-pin-status">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="ws-pin-list" role="group" aria-label="Pick one">
      {options.map((option) => {
        const active = option.id === selectedId;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onPick(option)}
            aria-pressed={active}
            className="ws-pin-pick"
          >
            <span className="ws-pin-pick-top">
              <span className="ws-pin-pick-title">{option.title}</span>
              {active ? <Check strokeWidth={2.25} aria-hidden="true" /> : null}
            </span>
            {option.subtitle ? <span className="ws-pin-pick-desc">{option.subtitle}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** The find field every picker shares: the kit field, under its FIND label. */
function FindField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  return (
    <KitInput
      label="Find"
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
    />
  );
}

function filterOptions(options: PickerOption[], query: string): PickerOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(
    (o) =>
      o.title.toLowerCase().includes(q) ||
      (o.subtitle ?? "").toLowerCase().includes(q),
  );
}

type PickerProps = {
  brandId: string;
  selectedId: string | null;
  onPick: (option: PickerOption) => void;
};

function FactPicker({ brandId, selectedId, onPick }: PickerProps) {
  const [query, setQuery] = useState("");
  const graph = useBrandUniverseGraph(brandId);
  const options = useMemo(
    () =>
      (graph.data?.nodes ?? [])
        .filter((n) => n.type === "fact")
        .map((n) => ({ id: n.id, title: n.name, subtitle: n.description })),
    [graph.data],
  );
  return (
    <div className="ws-pin-find">
      <FindField value={query} onChange={setQuery} placeholder="Search the universe facts" />
      <PickerList
        options={filterOptions(options, query)}
        isLoading={graph.isLoading}
        selectedId={selectedId}
        onPick={onPick}
        emptyText="No universe facts to pin yet. Grow the brand universe first."
      />
    </div>
  );
}

function StrategyPicker({ brandId, selectedId, onPick }: PickerProps) {
  const [query, setQuery] = useState("");
  const { strategies, isLoading } = useStrategies(brandId);
  const options = useMemo(
    () =>
      strategies.map((s) => ({
        id: s.id,
        title: s.title,
        subtitle: `${s.version} · ${s.status}`,
      })),
    [strategies],
  );
  return (
    <div className="ws-pin-find">
      <FindField value={query} onChange={setQuery} placeholder="Search strategies" />
      <PickerList
        options={filterOptions(options, query)}
        isLoading={isLoading}
        selectedId={selectedId}
        onPick={onPick}
        emptyText="No strategies yet. Create one on the Strategy page first."
      />
    </div>
  );
}

function SignalPicker({ brandId, selectedId, onPick }: PickerProps) {
  const [query, setQuery] = useState("");
  const { signals, isLoading } = useMarketSignals(brandId);
  const options = useMemo(
    () =>
      (signals ?? []).map((s) => ({
        id: s.id,
        title: s.title,
        subtitle: `${s.kind} · ${s.status}`,
      })),
    [signals],
  );
  return (
    <div className="ws-pin-find">
      <FindField value={query} onChange={setQuery} placeholder="Search signals" />
      <PickerList
        options={filterOptions(options, query)}
        isLoading={isLoading}
        selectedId={selectedId}
        onPick={onPick}
        emptyText="No market signals for this brand yet."
      />
    </div>
  );
}

function TrendPicker({ selectedId, onPick }: Omit<PickerProps, "brandId">) {
  const [query, setQuery] = useState("");
  // Newest, not the API's unnamed default: that one orders by a stored momentum
  // score the client never receives, so a picker sorted by it could not tell the
  // reader why one trend sits above another.
  const { trends, isLoading } = useTrends({
    q: query.trim() || undefined,
    pageSize: 20,
    sort: "newest",
  });
  const options = useMemo(
    () =>
      trends.map((t) => ({
        id: t.id,
        title: t.title,
        subtitle: [t.type, timeAgo(t.createdAt)].filter(Boolean).join(" · "),
      })),
    [trends],
  );
  return (
    <div className="ws-pin-find">
      <FindField value={query} onChange={setQuery} placeholder="Search trends" />
      <PickerList
        options={options}
        isLoading={isLoading}
        selectedId={selectedId}
        onPick={onPick}
        emptyText="No trends matched that search."
      />
    </div>
  );
}

type Props = {
  brandId: string;
  onClose: () => void;
  /** done(error?): close on success, surface the message on failure. */
  onSubmit: (vars: AddContextVars, done: (error?: string) => void) => void;
};

export default function AddContextModal({ brandId, onClose, onSubmit }: Props) {
  const [kind, setKind] = useState<WorkshopContextItemKind>("universe_fact");
  const { isGuest } = usePermissions();
  const kindItems = useMemo(
    () => (isGuest ? KIND_ITEMS.filter((item) => !GUEST_HIDDEN_KINDS.has(item.value)) : KIND_ITEMS),
    [isGuest],
  );
  const [targetId, setTargetId] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeKind = (next: WorkshopContextItemKind) => {
    setKind(next);
    setTargetId(null);
    setUrl("");
    setLabel("");
    setError(null);
  };

  const handlePick = (option: PickerOption) => {
    setTargetId(option.id);
    setLabel(option.title.slice(0, 160));
  };

  const trimmedLabel = label.trim();
  const canSubmit =
    !!trimmedLabel &&
    !submitting &&
    (kind === "url" ? url.trim().length > 0 : targetId !== null);

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    onSubmit(
      {
        kind,
        ...(kind === "url"
          ? { url: url.trim() }
          : { targetId: (targetId ?? "").trim() }),
        label: trimmedLabel,
      },
      (submitError) => {
        setSubmitting(false);
        if (submitError) setError(submitError);
      },
    );
  };

  return (
    <WorkshopDialog
      isOpen
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Pin to this project"
      description="Pin what Creo and deployed agents should always work from."
      className="ws-pin"
      cancelLabel="Cancel"
      confirmLabel="Pin"
      onSubmit={handleSubmit}
      confirmDisabled={!canSubmit}
      confirmLoading={submitting}
    >
      <div className="ws-pin-kinds">
        {/* No lens while A link is the pick: the segments then hold nothing. */}
        <KitSegmentedControl
          variant="lens"
          options={kindItems}
          value={kind}
          onChange={changeKind}
          ariaLabel="What to pin"
          className="h-10"
        />
        <button
          type="button"
          className="ws-pin-link"
          aria-pressed={kind === "url"}
          onClick={() => changeKind("url")}
        >
          <Link2 aria-hidden="true" />
          {KIND_SEGMENT.url}
        </button>
      </div>

      {kind === "universe_fact" ? (
        <FactPicker brandId={brandId} selectedId={targetId} onPick={handlePick} />
      ) : kind === "strategy" ? (
        <StrategyPicker brandId={brandId} selectedId={targetId} onPick={handlePick} />
      ) : kind === "market_signal" ? (
        <SignalPicker brandId={brandId} selectedId={targetId} onPick={handlePick} />
      ) : kind === "trend" ? (
        <TrendPicker selectedId={targetId} onPick={handlePick} />
      ) : (
        <KitInput
          label="Link"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
        />
      )}

      <KitInput
        label="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="How this shows up in the project"
        maxLength={160}
      />

      {error ? (
        <p role="alert" className="ws-field-error">
          {error}
        </p>
      ) : null}
    </WorkshopDialog>
  );
}
